import { ArrowDownOutlined, ArrowUpOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Card, Empty, Segmented, Space, Tooltip, Typography, theme as antTheme } from 'antd';
import { useState } from 'react';
import type { DashboardExpenses, DashboardTrends, DashboardTrendSeries } from '../../api/types';
import { formatNumber } from '../../lib/format';
import { MONTH_ABBR, TrendChart } from './TrendChart';

const { Text } = Typography;

/** Current year = blue solid, previous year = orange dashed — the convention
 * the reference widgets use, independent of the app's (green) primary color. */
const CURRENT_COLOR = '#1677ff';
const PREVIOUS_COLOR = '#fa8c16';

/** Big headline money: rounded to whole RON, ro-RO grouped ("150.196 RON").
 * Display-only `Number()` at the edge, never fed back into money math. */
function formatLeiRounded(value: string): string {
  return `${formatNumber(Math.round(Number(value)))} RON`;
}

/** The `month` leading points of a 12-length series, as plain numbers. */
function plot(values: string[], month: number): number[] {
  return values.slice(0, month).map(Number);
}

function LegendDot({ color }: { color: string }) {
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 6 }} />;
}

/** VÂNZĂRI / ÎNCASĂRI: current-year total, count + average-per-day, and a
 * single-series mini line chart of the year so far. */
export function MetricTrendCard({
  title,
  series,
  month,
  countLabel,
  loading,
}: {
  title: string;
  series: DashboardTrendSeries;
  month: number;
  countLabel: string;
  loading?: boolean;
}) {
  const months = MONTH_ABBR.slice(0, month) as unknown as string[];
  return (
    <Card title={title} loading={loading} styles={{ body: { paddingTop: 16 } }}>
      <div style={{ marginBottom: 4 }}>
        <Text type="secondary">
          <LegendDot color={CURRENT_COLOR} />
          Anul curent
        </Text>
      </div>
      <div style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.2 }} className="num">
        {formatLeiRounded(series.total)}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', margin: '12px 0 4px' }}>
        <div>
          <Text type="secondary" style={{ display: 'block' }}>
            {countLabel}
          </Text>
          <Text strong>{series.count}</Text>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Text type="secondary" style={{ display: 'block' }}>
            Valoare medie/zi
          </Text>
          <Text strong className="num">
            {formatLeiRounded(series.avgPerDay)}
          </Text>
        </div>
      </div>
      <TrendChart months={months} series={[{ data: plot(series.monthly, month), color: CURRENT_COLOR }]} height={160} xLabelEvery={2} />
    </Card>
  );
}

/** GRAFIC VÂNZĂRI / GRAFIC ÎNCASĂRI: current vs previous year (same Jan..month
 * window), the two totals, the % change, and a two-series line chart. */
export function ComparisonTrendCard({
  title,
  series,
  year,
  month,
  loading,
}: {
  title: string;
  series: DashboardTrendSeries;
  year: number;
  month: number;
  loading?: boolean;
}) {
  const {
    token: { colorSuccess, colorError },
  } = antTheme.useToken();
  const months = MONTH_ABBR.slice(0, month) as unknown as string[];
  const up = (series.growthPercent ?? 0) >= 0;

  return (
    <Card title={title} loading={loading} styles={{ body: { paddingTop: 16 } }}>
      <Space size="large" align="start" wrap style={{ marginBottom: 8 }}>
        <div>
          <Text type="secondary" style={{ display: 'block' }}>
            <LegendDot color={CURRENT_COLOR} />
            Anul curent ({year})
          </Text>
          <Text strong style={{ fontSize: 24 }} className="num">
            {formatLeiRounded(series.total)}
          </Text>
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block' }}>
            <LegendDot color={PREVIOUS_COLOR} />
            Anul anterior ({year - 1})
          </Text>
          <Text strong style={{ fontSize: 24 }} className="num">
            {formatLeiRounded(series.prevTotal)}
          </Text>
        </div>
        {series.growthPercent !== null ? (
          <div style={{ color: up ? colorSuccess : colorError, fontWeight: 600, fontSize: 16, paddingTop: 18 }}>
            {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(series.growthPercent)}%
          </div>
        ) : null}
      </Space>
      <TrendChart
        months={months}
        series={[
          { data: plot(series.monthly, month), color: CURRENT_COLOR },
          { data: plot(series.prevMonthly, month), color: PREVIOUS_COLOR, dashed: true },
        ]}
        height={230}
      />
    </Card>
  );
}

function LegendSwatch({ color, dashed, striped }: { color: string; dashed?: boolean; striped?: boolean }) {
  if (dashed) {
    return <span style={{ display: 'inline-block', width: 14, borderTop: `2px dashed ${color}`, marginRight: 6, verticalAlign: 'middle' }} />;
  }
  return (
    <span
      style={{
        display: 'inline-block',
        width: 12,
        height: 10,
        marginRight: 6,
        verticalAlign: 'middle',
        borderRadius: 2,
        background: striped ? `repeating-linear-gradient(45deg, ${color}, ${color} 3px, transparent 3px, transparent 6px)` : color,
        border: striped ? `1px solid ${color}` : undefined,
      }}
    />
  );
}

// --- CHELTUIELI pe categorii ------------------------------------------------

/**
 * CHELTUIELI: the SmartBill-style category breakdown — one bar per category
 * (amount + % of the period's total), with an An curent / Luna aceasta
 * period switch. "Fără categorie" collects the uncategorized ones.
 */
export function ExpenseCategoriesCard({ expenses, loading }: { expenses: DashboardExpenses; loading?: boolean }) {
  const {
    token: { colorFillSecondary },
  } = antTheme.useToken();
  const [period, setPeriod] = useState<'year' | 'month'>('year');

  const rows = expenses.byCategory[period];
  const total = rows.reduce((sum, row) => sum + Number(row.total), 0);

  return (
    <Card
      title="CHELTUIELI"
      loading={loading}
      extra={
        <Segmented<'year' | 'month'>
          size="small"
          options={[
            { label: 'Anul curent', value: 'year' },
            { label: 'Luna aceasta', value: 'month' },
          ]}
          value={period}
          onChange={setPeriod}
        />
      }
    >
      <div style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.2, marginBottom: 16 }} className="num">
        {formatLeiRounded(String(total))}
      </div>
      {rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nicio cheltuială în perioada selectată." />
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {rows.map((row) => {
            const pct = total > 0 ? (Number(row.total) / total) * 100 : 0;
            return (
              <div key={row.categoryId ?? '__none__'}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text>{row.name ?? 'Fără categorie'}</Text>
                  <Text className="num">
                    {formatLeiRounded(row.total)}{' '}
                    <Text type="secondary" className="num">
                      {Math.round(pct)}%
                    </Text>
                  </Text>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: colorFillSecondary, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: CURRENT_COLOR, borderRadius: 4 }} />
                </div>
              </div>
            );
          })}
        </Space>
      )}
    </Card>
  );
}

// --- PROFIT (estimare) ------------------------------------------------------

function ProfitRow({ label, value, strong, danger }: { label: React.ReactNode; value: string; strong?: boolean; danger?: boolean }) {
  const isNegative = Number(value) < 0;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
      <Text type={danger ? 'secondary' : undefined} strong={strong}>
        {label}
      </Text>
      <Text strong={strong} className="num" style={isNegative ? { color: '#cf1322' } : undefined}>
        {formatLeiRounded(value)}
      </Text>
    </div>
  );
}

/**
 * PROFIT: the current-year estimate — venituri facturate (fără TVA) minus
 * cheltuieli, then the net after a hypothetical full dividend distribution at
 * the global rate from Setări. Profit tax is NOT subtracted here: it is
 * already recorded among the expenses (plăți către Trezorerie), so an estimate
 * on top would double-count it. Explicitly an ORIENTATION figure
 * (deductibility rules are out of scope) — the tooltip says so.
 */
export function ProfitCard({ expenses, year, loading }: { expenses: DashboardExpenses; year: number; loading?: boolean }) {
  const {
    token: { colorSuccess, colorSplit },
  } = antTheme.useToken();
  const { profit } = expenses;
  const positive = Number(profit.profitNet) >= 0;

  return (
    <Card
      title={
        <>
          PROFIT ({year}){' '}
          <Tooltip title="Estimare de orientare pe baza facturilor emise (fără TVA) și a cheltuielilor înregistrate — nu înlocuiește calculul contabil (deductibilitate, ajustări).">
            <QuestionCircleOutlined style={{ fontSize: 14 }} />
          </Tooltip>
        </>
      }
      loading={loading}
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.2, color: positive ? colorSuccess : '#cf1322' }} className="num">
          {formatLeiRounded(profit.profitNet)}
          <Text type="secondary" style={{ fontSize: 14, fontWeight: 400, marginLeft: 8 }}>
            profit net estimat
          </Text>
        </div>
        <Text type="secondary" style={{ fontSize: 13 }}>
          ≈ {formatLeiRounded(profit.medieLunara)} / lună (medie)
        </Text>
      </div>
      <ProfitRow label="Venituri facturate (fără TVA)" value={profit.venit} />
      <ProfitRow label="Cheltuieli" value={`-${profit.cheltuieli}`} />
      <div style={{ borderTop: `1px solid ${colorSplit}` }} />
      <ProfitRow label="Profit net" value={profit.profitNet} strong />
      <div style={{ borderTop: `1px solid ${colorSplit}` }} />
      <ProfitRow label={`Impozit dividende (${profit.dividendTaxPercent}%)`} value={`-${profit.impozitDividende}`} danger />
      <ProfitRow label="Net după distribuirea dividendelor" value={profit.netDupaDividende} strong />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          ≈ {formatLeiRounded(profit.medieLunaraDupaDividende)} / lună (medie)
        </Text>
      </div>
    </Card>
  );
}

/**
 * PLAFON TVA: current-year net turnover against the RO VAT-exemption ceiling.
 * A single track carries three things at once:
 *  - the solid **facturat** fill (amber past 80%, red at/over the limit);
 *  - a striped **estimat la final de an** extension = facturat + what the
 *    active subscriptions will still invoice by 31 Dec (red if it would breach
 *    the ceiling);
 *  - an **azi** marker at the fraction of the year already elapsed, so the
 *    billing pace can be read against the calendar pace at a glance.
 */
export function VatThresholdCard({ vatThreshold, loading }: { vatThreshold: DashboardTrends['vatThreshold']; loading?: boolean }) {
  const {
    token: { colorText, colorWarning, colorError, colorInfo, colorFillSecondary, colorTextTertiary },
  } = antTheme.useToken();

  const { percent, projectedPercent, yearFraction } = vatThreshold;
  const factPct = Math.min(100, Math.max(0, percent));
  const projPct = Math.min(100, Math.max(0, projectedPercent));
  const yearPct = Math.min(100, Math.max(0, yearFraction * 100));
  const projectedBreaches = projectedPercent >= 100;

  const barColor = percent >= 100 ? colorError : percent >= 80 ? colorWarning : colorText;
  const projectedColor = projectedBreaches ? colorError : colorInfo;

  return (
    <Card title="Plafon TVA" loading={loading}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Text type="secondary" style={{ display: 'block' }}>
            Valoare Plafon
          </Text>
          <Text strong style={{ fontSize: 20 }} className="num">
            {formatLeiRounded(vatThreshold.threshold)}
          </Text>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Text type="secondary" style={{ display: 'block' }}>
            Facturat{' '}
            <Tooltip title="Cifra de afaceri facturată (fără TVA) în anul curent, comparată cu plafonul de scutire de TVA.">
              <QuestionCircleOutlined />
            </Tooltip>
          </Text>
          <Text strong style={{ fontSize: 20 }} className="num">
            {formatLeiRounded(vatThreshold.invoiced)}
          </Text>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <Text type="secondary">Facturat</Text>
        <Text strong className="num" style={{ color: barColor }}>
          {percent.toFixed(2)}%
        </Text>
      </div>

      {/* The track, with room above for the "azi" caption. */}
      <div style={{ position: 'relative', paddingTop: 16 }}>
        {/* "azi" time-position marker: a dashed vertical line + caption. */}
        <div style={{ position: 'absolute', left: `${yearPct}%`, top: 4, bottom: 0, borderLeft: `2px dashed ${colorTextTertiary}` }} />
        <div
          style={{ position: 'absolute', left: `${yearPct}%`, top: 0, transform: 'translateX(-50%)', fontSize: 11, color: colorTextTertiary, whiteSpace: 'nowrap' }}
        >
          azi
        </div>

        <div style={{ position: 'relative', height: 16, borderRadius: 8, background: colorFillSecondary, overflow: 'hidden' }}>
          {/* Projected total (striped), drawn behind the solid actual fill. */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${projPct}%`,
              background: `repeating-linear-gradient(45deg, ${projectedColor}, ${projectedColor} 4px, transparent 4px, transparent 8px)`,
              opacity: 0.6,
            }}
          />
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${factPct}%`, background: barColor, borderRadius: 8 }} />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <Text type="secondary">Limită</Text>
        <Text type="secondary" className="num">
          {formatLeiRounded(vatThreshold.threshold)}
        </Text>
      </div>

      {/* Legend: what the striped extension and the dashed marker mean. */}
      <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: '4px 20px' }}>
        <Text type="secondary">
          <LegendSwatch color={barColor} /> Facturat — {formatLeiRounded(vatThreshold.invoiced)}
        </Text>
        <Text type="secondary" style={{ color: projectedBreaches ? colorError : undefined }}>
          <LegendSwatch color={projectedColor} striped /> Estimat la final de an (cu abonamente) — {formatLeiRounded(vatThreshold.projected)} ({projectedPercent.toFixed(2)}%)
        </Text>
        <Text type="secondary">
          <LegendSwatch color={colorTextTertiary} dashed /> Azi — {(yearFraction * 100).toFixed(0)}% din an
        </Text>
      </div>

      {projectedBreaches ? (
        <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
          Estimarea depășește plafonul de scutire — verifică dacă firma trebuie să se înregistreze în scopuri de TVA.
        </Text>
      ) : null}
    </Card>
  );
}
